import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { isNaN } from 'lodash';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { validateMaxDecimals } from '../../../../utils';
import { InputFieldProps } from '../../../../components/forms/fields/InputField';
import { InputField } from '../../../../components/forms/fields';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { STOCK_QUANTITY_PRECISION } from '../../../../services/price-service/constants';
import { ProductFormValues } from './types';

interface WasteFieldProps {
    quantity: number;
    absoluteWasteFieldProps: InputFieldProps<ProductFormValues>;
    relativeWasteFieldProps: InputFieldProps<ProductFormValues>;
    isDisabled?: boolean;
    maxDecimals: number;
}

const MAX_ABSOLUTE_WASTE = 99999;

const WasteField = ({
    maxDecimals,
    isDisabled,
    quantity,
    absoluteWasteFieldProps,
    relativeWasteFieldProps,
}: WasteFieldProps) => {
    const { t } = useTranslation();
    const { priceService } = usePriceService();
    const [, { value: absoluteFieldValue }, { setValue: setAbsoluteFieldValue }] = useField(absoluteWasteFieldProps.name);
    const [, { value: relativeFieldValue }, { setValue: setRelativeFieldValue }] = useField(relativeWasteFieldProps.name);
    const [absoluteValue, setAbsoluteValue] = useState<number | string>(absoluteFieldValue || 0);
    const previousQuantity = useRef(quantity);
    // Calculate absoluteValue / quantity = percentage
    const [relativeValue, setRelativeValue] = useState<number | string>(relativeFieldValue && quantity
        ? priceService.calculatePercentage(absoluteFieldValue, quantity, { precision: STOCK_QUANTITY_PRECISION })
        : 0);

    useEffect(() => {
        if (previousQuantity.current !== quantity){
            const absoluteWaste = priceService.percentageFrom(quantity, relativeFieldValue);
            setAllAbsoluteValueFields(absoluteWaste);
            previousQuantity.current = quantity;
        }
    // eslint-disable-next-line
    }, [quantity]);

    const setAllAbsoluteValueFields = (absoluteValueQuantity: number | string) => {
        setAbsoluteValue(absoluteValueQuantity);
        setAbsoluteFieldValue(absoluteValueQuantity);
    };

    const setAllRelativeValueFields = (relativeValueQuantity: number | string) => {
        setRelativeValue(relativeValueQuantity);
        setRelativeFieldValue(relativeValueQuantity);
    };

    const handleAbsoluteWasteChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const newValue = parseFloat(e.target.value);
        const validatedValue = validateMaxDecimals({ value: newValue, maxDecimals });

        if (validatedValue > quantity || validatedValue > MAX_ABSOLUTE_WASTE || validatedValue < 0) {
            setAllRelativeValueFields(100);
            setAllAbsoluteValueFields(quantity);
            return;
        }

        if (isNaN(validatedValue)) {
            setAllAbsoluteValueFields('');
            setAllRelativeValueFields('');
            return;
        }

        const wastePercentage = priceService.calculatePercentage(validatedValue, quantity, { precision: STOCK_QUANTITY_PRECISION });
        setAllAbsoluteValueFields(validatedValue);
        setAllRelativeValueFields(wastePercentage);
    };

    const handleRelativeWasteChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        const newValue = parseFloat(e.target.value);
        const validatedValue = validateMaxDecimals({ value: newValue, maxDecimals: 2 });

        if (validatedValue > 100) {
            setAllRelativeValueFields(100);
            setAllAbsoluteValueFields(quantity);
            return;
        }

        if (validatedValue < 0) {
            setAllRelativeValueFields(0);
            setAllAbsoluteValueFields(0);
            return;
        }

        if (isNaN(validatedValue)) {
            setAllAbsoluteValueFields('');
            setAllRelativeValueFields('');
            return;
        }

        const absoluteWaste = priceService.percentageFrom(quantity, validatedValue);
        setAllAbsoluteValueFields(absoluteWaste);
        setAllRelativeValueFields(validatedValue);
    };


    return (
        <Box flex="1" display="flex">
            <InputField
                {...relativeWasteFieldProps}
                tooltip={t('% of from quantity which is waited for this item')}
                onChange={handleRelativeWasteChange}
                value={relativeValue}
                disabled={isDisabled}
                type="number"
            />
            <Box mr={2} />
            <InputField
                {...absoluteWasteFieldProps}
                tooltip={t('Quantity which going to be wasted for this item')}
                onChange={handleAbsoluteWasteChange}
                value={absoluteValue}
                disabled={isDisabled}
                type="number"
            />
        </Box>
    );
};

export default WasteField;